import { useCallback, useEffect } from 'react'

import { DEFAULT_USER_LOCATION } from '@/constants/location'
import { convertLocationToLatestSearchCookie } from '@/domain/location/convertLocationToLatestSearchCookie'
import { getRecentLocationsFromLocalStorage } from '@/domain/location/getRecentLocationsFromLocalStorage'
import { setModifiedRadiusLocalStorage } from '@/domain/location/setModifiedRadiusLocalStorage'
import { type UserLocation, useGetUserLocationQuery } from '@/generated/graphql-types'
import { updateLatestSearchCookie } from '@/utils/cookies/latestSearchCookie'
import { updateUserLocationCache } from '@/utils/location/location'

export const useGetLocation = (): {
  location: UserLocation
  updateUserLocation: (newLocation: UserLocation) => void
} => {
  const { data: { userLocation } = {}, client } = useGetUserLocationQuery()

  const location = userLocation || DEFAULT_USER_LOCATION

  const updateUserLocation = useCallback(
    (newLocation: UserLocation) => {
      /** Update Cache */
      updateUserLocationCache(newLocation, client)

      /** Update cookies */
      const legacyCookieLocation = convertLocationToLatestSearchCookie(newLocation)
      updateLatestSearchCookie(legacyCookieLocation)

      /** Should reset local storage value if user change location to a region*/
      location.isRegion && setModifiedRadiusLocalStorage(false)
    },
    [client, location.isRegion]
  )

  /** Get recent search locations from localStorage once window is defined.
   * FES always sets a location radius regardless of whether the user selected a radius
   * or not. Consequently, we must verify whether the set radius is valid by
   * checking if the area is a region. The "isSearchAllArea" property is set to true
   * for region locations.
   */
  useEffect(() => {
    /** Always use the getRecentLocationsFromLocalStorage method to fetch the latest recent search locations from localStorage.
     * Hooks create a new state every time they are invoked. The recentSearchLocations array exposed by the
     * useLocationSearchSuggestions hook will *not* return an updated array of recent search locations.
     */
    const recentSearchLocations = getRecentLocationsFromLocalStorage()
    const isSearchAllArea = recentSearchLocations?.[0]?.isSearchAllArea ?? false

    if (location.isRegion !== isSearchAllArea) {
      updateUserLocation({
        ...location,
        isRegion: isSearchAllArea,
      })
    }
  }, [location, updateUserLocation])

  return { location, updateUserLocation }
}
